import React from "react";
import moment from "moment";
import { Tooltip } from "@mui/material";

import { ReactComponent as ActiveControl } from "assets/img/svg/ActiveControl.svg";
import { ReactComponent as InactiveControl } from "assets/img/svg/InactiveControl.svg";

import CustomButton from "components/CustomButton";
import AChip from "../../../common/chip/AChip";
import ADataGrid from "../../../common/data-grid/ADataGrid";
import { AGridColDef } from "../../../common/data-grid/ADataGridColDef";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import { getValueById } from "../../pincode/constants";
import {
  paymentOptions,
  creditNoteOptions,
  indicatorTypes,
  accountTypes,
} from "views/pages/manage/unAssigned/paymentMode/accountingEntrySetup/utils";

const InvoiceEntryTableContent = ({
  type,
  rows,
  rowForm,
  setRowForm,
  rowModesModel,
  startEditing,
  saveRow,
  handleCancel,
  accountOptions,
  userPermissions,
  isLoading,
}) => {
  const handleChange = (val, key) => {
    let obj = {};
    if (key === "accountNumber") {
      obj.accountNumber = val.value;
      obj.description = val.description;
    } else if (key === "sfglAccountType") {
      obj.sfglAccountType = val.value;
      obj.sfglAccountKey = val.label;
    } else {
      obj[key] = val;
    }

    setRowForm((prev) => ({ ...prev, ...obj }));
  };

  const isSaveDisabled =
    isLoading ||
    !rowForm ||
    !rowForm.sfglAccountType ||
    !rowForm.debitCredit ||
    !rowForm.accountNumber ||
    (type === "POSITIVE" ? !rowForm.paymentStatus : !rowForm.creditNoteType);

  const baseColumns = [
    type === "POSITIVE"
      ? new AGridColDef("paymentStatus", "Payment Status")
      : new AGridColDef("creditNoteType", "Credit Note Type"),
  ].map((col) =>
    col
      .setMinWidth(100)
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <div className="w-100">
          <AAutoComplete
            items={type === "POSITIVE" ? paymentOptions : creditNoteOptions}
            handleChange={(val) =>
              handleChange(
                val?.value,
                type === "POSITIVE" ? "paymentStatus" : "creditNoteType",
              )
            }
            currentValue={getValueById(
              type === "POSITIVE" ? paymentOptions : creditNoteOptions,
              type === "POSITIVE"
                ? rowForm?.paymentStatus
                : rowForm?.creditNoteType,
            )}
            isShowCount={false}
            isMulti={false}
          />
        </div>
      )),
  );

  const columns = [
    ...baseColumns,

    new AGridColDef("sfglAccountType", "SFGL Account Type")
      .setMinWidth(100)
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <div className="w-100">
          <AAutoComplete
            items={accountTypes}
            handleChange={(val) => handleChange(val, "sfglAccountType")}
            currentValue={getValueById(accountTypes, rowForm?.sfglAccountType)}
            isShowCount={false}
            isMulti={false}
          />
        </div>
      )),

    new AGridColDef("debitCredit", "Debit/Credit")
      .setMinWidth(100)
      .editable(true)
      .renderCellComponent((p) => (
        <div className="text-capitalize">{p.value || "-"}</div>
      ))
      .renderEditCellComponent(() => (
        <div className="w-100">
          <AAutoComplete
            items={indicatorTypes}
            handleChange={(val) => handleChange(val?.value, "debitCredit")}
            currentValue={getValueById(indicatorTypes, rowForm?.debitCredit)}
            isShowCount={false}
            isMulti={false}
          />
        </div>
      )),

    new AGridColDef("accountNumber", "Account")
      .setMinWidth(100)
      .editable(true)
      .renderEditCellComponent(() => (
        <div className="w-100">
          <AAutoComplete
            items={accountOptions}
            handleChange={(val) => handleChange(val, "accountNumber")}
            currentValue={getValueById(accountOptions, rowForm?.accountNumber)}
            isShowCount={false}
            isMulti={false}
          />
        </div>
      )),

    new AGridColDef("description", "Description")
      .setMinWidth(150)
      .editable(true)
      .renderCellComponent((params) => {
        return (
          <Tooltip title={params?.value || "-"}>{params?.value || "-"}</Tooltip>
        );
      })
      .renderEditCellComponent(() => (
        <Tooltip title={rowForm.description}>
          <div className="ellipsis-text-container">{rowForm?.description}</div>
        </Tooltip>
      )),

    new AGridColDef("effectiveDate", "Effective Date")
      .setMinWidth(80)
      .renderCellComponent((p) => moment(p.value).format("DD/MM/YYYY")),

    new AGridColDef("status", "Status")
      .setMinWidth(100)
      .editable(true)
      .renderCellComponent((p) => (
        <AChip label={p.value} success={p.value === "ACTIVE"} />
      ))
      .renderEditCellComponent((params) => {
        return (
          <div>
            <span>
              {rowForm?.status === "ACTIVE" ? (
                <ActiveControl
                  onClick={() => handleChange("INACTIVE", "status")}
                />
              ) : (
                <InactiveControl
                  onClick={() => handleChange("ACTIVE", "status")}
                />
              )}
              <span className="ml-1">Active</span>
            </span>
          </div>
        );
      }),

    new AGridColDef("actions", "Actions").renderCellComponent((params) => {
      const isEdit = rowModesModel[params.id]?.mode === "edit";

      return isEdit ? (
        <>
          <CustomButton
            content="Save"
            permissionType={"C,U"}
            icon={false}
            forTable={true}
            permissionSet={userPermissions}
            disabled={isSaveDisabled}
            onClick={() => saveRow(params.id, type)}
          />
          <CustomButton
            content=""
            permissionType={"cancel"}
            icon={true}
            forTable={true}
            permissionSet={userPermissions}
            onClick={() => handleCancel(params, type)}
          />
        </>
      ) : (
        <CustomButton
          content="Edit"
          permissionType={"U"}
          icon={true}
          forTable={true}
          permissionSet={userPermissions}
          onClick={() => startEditing(params.id, params.row)}
        />
      );
    }),
  ];

  return (
    <div style={{ marginBottom: "30px" }}>
      <ADataGrid
        rows={rows}
        rowCount={rows?.length}
        columns={columns}
        rowModesModel={rowModesModel}
        rowId={(row) => row?.id}
      />
    </div>
  );
};

export default InvoiceEntryTableContent;
